import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import InvoiceLifecycleWizard from '../components/InvoiceLifecycleWizard';
import {
  ArrowRight, User, Mail, Phone, MapPin, Hash, Building2,
  FileText, FolderKanban, Receipt, Banknote, RotateCcw, Archive,
  Loader2, Activity, CheckCircle2, AlertTriangle, ExternalLink,
} from 'lucide-react';

const EVENT_VISUAL = {
  customer_created:  { icon: User,         color: 'slate',   label_ar: 'إنشاء العميل' },
  quotation:         { icon: FileText,     color: 'indigo',  label_ar: 'عرض سعر' },
  project_created:   { icon: FolderKanban, color: 'emerald', label_ar: 'مشروع' },
  invoice:           { icon: Receipt,      color: 'sky',     label_ar: 'فاتورة' },
  payment:           { icon: Banknote,     color: 'emerald', label_ar: 'دفعة' },
  credit_note:       { icon: RotateCcw,    color: 'amber',   label_ar: 'إشعار دائن' },
  archive_action:    { icon: Archive,      color: 'rose',    label_ar: 'أرشفة' },
};

const COLOR_CLASSES = {
  slate:   { bg: 'bg-slate-500',   ring: 'ring-slate-200',   text: 'text-slate-700',   light: 'bg-slate-50',   border: 'border-slate-200' },
  sky:     { bg: 'bg-sky-500',     ring: 'ring-sky-200',     text: 'text-sky-700',     light: 'bg-sky-50',     border: 'border-sky-200' },
  emerald: { bg: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200' },
  amber:   { bg: 'bg-amber-500',   ring: 'ring-amber-200',   text: 'text-amber-700',   light: 'bg-amber-50',   border: 'border-amber-200' },
  rose:    { bg: 'bg-rose-500',    ring: 'ring-rose-200',    text: 'text-rose-700',    light: 'bg-rose-50',    border: 'border-rose-200' },
  indigo:  { bg: 'bg-indigo-500',  ring: 'ring-indigo-200',  text: 'text-indigo-700',  light: 'bg-indigo-50',  border: 'border-indigo-200' },
};

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function CustomerTimeline() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [openInvoice, setOpenInvoice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await axiosInstance.get(`/customers/${customerId}/timeline`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      toast.error('فشل تحميل Timeline العميل');
    } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { if (customerId) load(); }, [customerId, load]);

  const events = data?.events || [];
  const visible = filterType === 'all' ? events : events.filter(e => e.type === filterType);
  const types = Array.from(new Set(events.map(e => e.type)));

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="customer-timeline-loading">
      <div className="text-center text-slate-500">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" />
        <p>جاري تحميل دورة حياة العميل...</p>
      </div>
    </div>
  );

  if (err) return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-2xl mx-auto bg-rose-50 border border-rose-200 rounded-xl p-6 text-rose-800">
        <AlertTriangle className="w-6 h-6 mb-2" />
        <h2 className="font-bold mb-1">تعذّر تحميل العميل</h2>
        <p className="text-sm">{err}</p>
        <button onClick={() => navigate('/customers')} className="mt-3 text-sm underline">العودة للعملاء</button>
      </div>
    </div>
  );

  const c = data.customer;
  const s = data.summary;

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="customer-timeline-page">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-5">
        <button
          onClick={() => navigate('/customers')}
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-3"
          data-testid="back-to-customers"
        >
          <ArrowRight className="w-4 h-4" />
          العودة إلى العملاء
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          <Activity className="w-7 h-7 text-sky-600" />
          <h1 className="text-2xl font-bold text-slate-900">دورة حياة العميل · Customer 360°</h1>
          {c.is_archived && (
            <span className="px-3 py-1 rounded-md text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-300">
              مؤرشف
            </span>
          )}
        </div>
      </div>

      {/* Customer card */}
      <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white flex items-center justify-center text-xl font-bold shadow-md">
            {(c.name || '?').charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-slate-900" data-testid="customer-name">{c.name}</h2>
            {c.name_arabic && c.name_english && (
              <p className="text-sm text-slate-500">{c.name_arabic} · {c.name_english}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
              {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
              {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
              {c.vat_number && <span className="flex items-center gap-1"><Hash className="w-3 h-3" />VAT: {c.vat_number}</span>}
              {c.customer_type && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{c.customer_type}</span>}
              {c.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.address}</span>}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">أُنشئ في {fmtDate(c.created_at)}</p>
          </div>
        </div>
      </div>

      {/* Financial summary */}
      <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
        {[
          { label: 'عروض الأسعار',    value: s.total_quotations,      type: 'count',  color: 'indigo' },
          { label: 'مشاريع نشطة',     value: s.active_projects,       type: 'count',  color: 'emerald' },
          { label: 'إجمالي المشاريع', value: s.total_projects,        type: 'count',  color: 'slate' },
          { label: 'فواتير',          value: s.total_invoices,        type: 'count',  color: 'sky' },
          { label: 'قيمة الفواتير',   value: s.total_invoice_value,   type: 'money',  color: 'slate' },
          { label: 'مدفوع',           value: s.total_paid,            type: 'money',  color: 'emerald' },
          { label: 'إشعارات دائنة',   value: s.total_credited,        type: 'money',  color: 'amber' },
          { label: 'متبقّي',          value: s.outstanding,           type: 'money',  color: s.outstanding > 0 ? 'rose' : 'emerald' },
        ].map((cell, i) => (
          <div
            key={i}
            className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-sm"
            data-testid={`summary-${cell.label}`}
          >
            <div className="text-[10px] text-slate-500 uppercase font-semibold">{cell.label}</div>
            <div className={`text-lg font-bold mt-1 ${COLOR_CLASSES[cell.color]?.text || 'text-slate-700'}`}>
              {cell.type === 'money' ? fmtMoney(cell.value) : cell.value}
            </div>
          </div>
        ))}
      </div>

      {/* Filter chips */}
      <div className="max-w-5xl mx-auto bg-white rounded-xl border border-slate-200 p-3 mb-3 flex flex-wrap gap-1.5 shadow-sm">
        <button
          onClick={() => setFilterType('all')}
          className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
            filterType === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
          data-testid="filter-all"
        >
          الكل ({events.length})
        </button>
        {types.map(t => {
          const count = events.filter(e => e.type === t).length;
          const v = EVENT_VISUAL[t] || EVENT_VISUAL.customer_created;
          const c = COLOR_CLASSES[v.color];
          const active = filterType === t;
          return (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                active
                  ? `${c.bg} text-white border-transparent`
                  : `bg-white ${c.text} ${c.border} hover:${c.light}`
              }`}
              data-testid={`filter-${t}`}
            >
              {v.label_ar} ({count})
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        {visible.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>لا توجد أحداث في هذا التصنيف</p>
          </div>
        ) : (
          <ol className="relative space-y-4">
            <div className="absolute right-5 top-2 bottom-2 w-0.5 bg-slate-200" />
            {visible.map((ev, idx) => {
              const v = EVENT_VISUAL[ev.type] || EVENT_VISUAL.customer_created;
              const cc = COLOR_CLASSES[v.color];
              const Icon = v.icon;
              const clickable = ev.type === 'invoice';
              return (
                <li
                  key={idx}
                  className={`relative flex items-start gap-3 ${clickable ? 'cursor-pointer' : ''}`}
                  data-testid={`timeline-event-${ev.type}`}
                  onClick={clickable ? () => setOpenInvoice(ev.link?.id) : undefined}
                >
                  <div className={`relative shrink-0 w-10 h-10 rounded-full ${cc.bg} text-white flex items-center justify-center shadow ring-4 ${cc.ring} ml-auto order-2`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className={`flex-1 ${cc.light} border ${cc.border} rounded-xl px-4 py-3 ${clickable ? 'hover:shadow-md transition-shadow' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className={`font-semibold ${cc.text} text-sm flex items-center gap-2`}>
                          {ev.label}
                          {clickable && <ExternalLink className="w-3 h-3 opacity-60" />}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                          <span>{fmtDate(ev.timestamp)}</span>
                          {ev.actor && <span>· بواسطة {ev.actor}</span>}
                        </div>
                      </div>
                      {ev.amount != null && (
                        <div className={`text-sm font-bold ${cc.text} shrink-0`}>
                          {fmtMoney(ev.amount)} ر.س
                        </div>
                      )}
                    </div>
                    {ev.meta && Object.keys(ev.meta).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                        {Object.entries(ev.meta).filter(([_, vv]) => vv != null && vv !== '').map(([k, vv]) => (
                          <span key={k} className="bg-white/70 text-slate-600 px-1.5 py-0.5 rounded font-mono">
                            {k}: {String(vv).slice(0, 40)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
            {/* Terminal */}
            {events.length > 0 && filterType === 'all' && (
              <li className="relative flex items-center gap-3 pt-2">
                <div className={`shrink-0 w-10 h-10 rounded-full ${c.is_archived ? 'bg-rose-100 text-rose-600 ring-rose-200' : 'bg-slate-100 text-slate-400 ring-slate-200'} flex items-center justify-center ring-4 ml-auto order-2`}>
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div className={`flex-1 text-sm italic ${c.is_archived ? 'text-rose-600' : 'text-slate-400'}`}>
                  {c.is_archived
                    ? `العميل مؤرشف${c.archive_reason ? ' — ' + c.archive_reason : ''}`
                    : 'العميل نشط — تواصل مستمر'}
                </div>
              </li>
            )}
          </ol>
        )}
      </div>

      {openInvoice && (
        <InvoiceLifecycleWizard invoiceId={openInvoice} onClose={() => setOpenInvoice(null)} />
      )}
    </div>
  );
}
